/**
 * The frame renderer of focus windows drawn above the wall: solid paper/ink
 * windows at screen scale, chrome state from the focus presentation, window
 * resize handles and the opened item's ⋯, Focus/Maximize and right-click.
 */
import { useMemo } from "react";
import {
  FRAME_CHROME_INSIDE_TOP,
  type FrameChromeGeometry,
  type FrameChromeState,
  type FrameRenderer,
} from "@/components/shapes/shared/frame-renderer-context";
import { presentedWindowBox } from "@/lib/focus/focus-windows";
import { useAboveWall } from "@/lib/focus/use-above-wall";
import { usePresentationStore } from "@/stores/presentation-store";
import { FocusWindowMenu } from "./focus-window-menu";
import { itemContextMenu } from "./item-context-menu";
import { FrameWindowResizeHandles } from "./focus-window-resize";
import { useFocusChromeState } from "./use-focus-chrome-state";

const CHROME_INSIDE: FrameChromeGeometry = { inside: true, scale: 1 };
const CHROME_ABOVE: FrameChromeGeometry = { inside: false, scale: 1 };

/** Chrome goes inside a window whose top is too close to the surface top. */
function useOverlayChromeGeometry(id: string): FrameChromeGeometry {
  const inside = usePresentationStore((state) => {
    const box = presentedWindowBox(state.presentation, id);
    return !!box && box.y < FRAME_CHROME_INSIDE_TOP;
  });
  return inside ? CHROME_INSIDE : CHROME_ABOVE;
}

/**
 * The focus chrome state; a one-item preview above the wall leaves out ←,
 * which would repeat × and the centred Back pill (`focus-overlay.tsx`).
 */
function useOverlayChromeState(id: string): FrameChromeState {
  const state = useFocusChromeState(id);
  const aboveWall = useAboveWall();
  const alone = usePresentationStore(
    (store) => store.presentation?.frameIds.length === 1,
  );
  const backHidden = aboveWall && alone;
  return useMemo(() => ({ ...state, backHidden }), [state, backHidden]);
}

export const overlayFrameRenderer: FrameRenderer = {
  useChromeState: useOverlayChromeState,
  useChromeGeometry: useOverlayChromeGeometry,
  useBackingScale: () => 1,
  material: "solid",
  independentSurface: true,
  renderControls: (props) => <FrameWindowResizeHandles {...props} />,
  renderMenu: (props) => <FocusWindowMenu {...props} />,
  contextMenu: itemContextMenu,
};
